import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import type { ImplementedRubricSpec } from '@/lib/types'

/**
 * Lets a rollout's result row open the same check on the page's rubric list.
 *
 * A page that shows both a rollout and a rubric provides the map its rubric list shows and
 * how to reveal it (each page switches tabs its own way). A result links only when it was
 * graded against that same map: a regrade or another package revision can number or word
 * its checks differently, and a link must never point at a different check.
 */
interface RubricLink {
  spec?: ImplementedRubricSpec | null
  open: (checkId: string) => void
}

const RubricLinkContext = createContext<(RubricLink & { revealRequest?: { criterionId: string } }) | null>(null)

export function RubricLinkProvider({ spec, open, children }: RubricLink & { children: ReactNode }) {
  const [revealRequest, setRevealRequest] = useState<{ criterionId: string }>()
  const reveal = useCallback((checkId: string) => {
    // A fresh request also handles opening the same check after collapsing it again.
    setRevealRequest({ criterionId: checkId })
    open(checkId)
  }, [open])
  const value = useMemo(() => ({ spec, open: reveal, revealRequest }), [spec, reveal, revealRequest])
  return <RubricLinkContext.Provider value={value}>{children}</RubricLinkContext.Provider>
}

/** Key order differs between the stored task and the frozen snapshot; the content does not. */
function stable(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stable((value as Record<string, unknown>)[key])}`).join(',')}}`
  }
  return JSON.stringify(value) ?? 'null'
}

/** How to open a check for results graded against `gradedWith`, or nothing if the page shows another map. */
export function useRubricLink(gradedWith?: ImplementedRubricSpec | null): ((checkId: string) => void) | undefined {
  const link = useContext(RubricLinkContext)
  return useMemo(() => {
    if (!link?.spec?.checks?.length || !gradedWith?.checks?.length) return undefined
    return stable(link.spec) === stable(gradedWith) ? link.open : undefined
  }, [link, gradedWith])
}

/** Let the matching rubric clear its filter and expand the requested check's section. */
export function useRubricRevealRequest(spec: ImplementedRubricSpec) {
  const link = useContext(RubricLinkContext)
  return link?.spec && stable(link.spec) === stable(spec) ? link.revealRequest : undefined
}

/** How long to wait for the tab that holds the check to render. */
const WAIT_MS = 1500
const HIGHLIGHT_MS = 2400
const highlights = new WeakMap<HTMLElement, number>()

/**
 * Scroll the check into view and highlight it, once the tab that holds it has rendered.
 * `scope` narrows the search to one page region; hidden copies of the list are skipped.
 */
export function revealRubricCheck(checkId: string, scope: () => ParentNode | null = () => document) {
  const deadline = performance.now() + WAIT_MS
  const attempt = () => {
    const candidates = [...(scope()?.querySelectorAll<HTMLElement>(`[data-rubric-check="${CSS.escape(checkId)}"]`) ?? [])]
    for (const element of candidates) {
      // A folded section is opened only if the section itself is on screen.
      const fold = element.closest('details')
      if (fold && !fold.open && fold.getClientRects().length) fold.open = true
    }
    const target = candidates.find((element) => element.getClientRects().length > 0)
    if (!target) {
      if (performance.now() < deadline) requestAnimationFrame(attempt)
      return
    }
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    target.scrollIntoView({ block: 'center', behavior: still ? 'auto' : 'smooth' })
    // Opening the same check again restarts its highlight rather than cutting it short.
    window.clearTimeout(highlights.get(target))
    target.classList.remove('rubric-check-flash')
    void target.offsetWidth
    target.classList.add('rubric-check-flash')
    target.tabIndex = -1
    target.focus({ preventScroll: true })
    highlights.set(target, window.setTimeout(() => target.classList.remove('rubric-check-flash'), HIGHLIGHT_MS))
  }
  requestAnimationFrame(attempt)
}
